/**
 * Core Checkers Game Types
 *
 * This file defines all TypeScript types and interfaces for the American Checkers
 * game implementation. It follows the design specified in
 * .kiro/specs/checkers-game/design.md
 */

// ============================================================================
// Basic Checkers Types
// ============================================================================

/**
 * Position on the checkers board
 * row: 0-7 (row 0 = top, dark's starting side)
 * col: 0-7
 */
export interface Position {
  row: number; // 0-7
  col: number; // 0-7
}

/**
 * Checkers piece types
 */
export type PieceType = 'regular' | 'king';

/**
 * Player colors
 */
export type PlayerColor = 'dark' | 'light';

/**
 * Checkers piece representation
 */
export interface CheckersPiece {
  color: PlayerColor;
  type: PieceType;
  position: Position;
}

/**
 * Checkers board representation (8x8 grid)
 * board[row][col] where row=0-7, col=0-7
 * Only dark squares ((row + col) % 2 === 1) are playable
 */
export type CheckersPieceGrid = (CheckersPiece | null)[][];

// ============================================================================
// Move Types
// ============================================================================

/**
 * Checkers move representation
 */
export interface CheckersMove {
  from: Position;
  to: Position;
  piece: CheckersPiece;
  isJump: boolean;
  jumpSequence?: Position[];       // Full path for multi-jumps: [start, land1, land2, ...]
  capturedPieces?: Position[];     // Positions of captured pieces
  isPromotion: boolean;            // Did the piece get kinged?
  notation: string;                // Board notation (e.g., "11-15" or "11x18x25")
  timestamp: number;
}

/**
 * Result of a move attempt
 */
export interface CheckersMoveResult {
  success: boolean;
  move?: CheckersMove;
  error?: string;
  gameStatus: CheckersGameStatus;
  multiJumpContinuation: boolean;  // True if player must continue jumping
  continuationMoves?: Position[];  // Available next jumps if continuation required
}

// ============================================================================
// Game State Types
// ============================================================================

/**
 * Game status types
 */
export type CheckersGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: PlayerColor; reason: 'capture-all' | 'no-moves' }
  | { type: 'draw'; reason: '40-move' | 'repetition' | 'insufficient-progress' }
  | { type: 'resignation'; winner: PlayerColor }
  | { type: 'draw-agreement' };

/**
 * Complete game state for persistence, synchronization, and serialization
 */
export interface CheckersGameState {
  board: CheckersPieceGrid;
  currentPlayer: PlayerColor;
  moveHistory: CheckersMove[];
  capturedPieces: { dark: number; light: number };
  gameStatus: CheckersGameStatus;
  movesSinceCapture: number;       // For 40-move draw rule
  movesSinceRegularMove: number;   // For 40-move draw rule
  positionHistory: string[];       // Serialized positions for repetition detection
  multiJumpInProgress: boolean;
  multiJumpPiece: Position | null; // Piece currently in a multi-jump
  kingOnlyMoveCount: number;       // For king-vs-king draw rule
}

// ============================================================================
// AI Types
// ============================================================================

/**
 * AI difficulty levels
 */
export type CheckersDifficulty = 'easy' | 'medium' | 'hard';

// ============================================================================
// Game Settings Types
// ============================================================================

/**
 * Game settings for mode selection
 */
export interface CheckersGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: CheckersDifficulty;
  isPrivate?: boolean;
}

// ============================================================================
// Game Result Types
// ============================================================================

/**
 * Game result for statistics tracking
 */
export interface CheckersGameResult {
  gameId: string;
  mode: CheckersGameSettings['mode'];
  difficulty?: CheckersDifficulty;
  winner?: PlayerColor;
  isDraw: boolean;
  reason: string;
  moveCount: number;
  duration: number; // seconds
  completedAt: number;
}

// ============================================================================
// Statistics Types
// ============================================================================

/**
 * Game statistics for a specific category
 */
export interface GameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageGameLength: number; // moves
}

/**
 * Complete checkers statistics
 */
export interface CheckersStatistics {
  userId?: string;
  singlePlayer: {
    easy: GameStats;
    medium: GameStats;
    hard: GameStats;
  };
  localMultiplayer: GameStats;
  onlineMultiplayer: GameStats;
  totalGames: number;
  totalPlayTime: number; // seconds
}

// ============================================================================
// Board Notation Helpers
// ============================================================================

/**
 * Standard checkers board notation numbers the 32 dark squares 1-32:
 *
 *     col0  col1  col2  col3  col4  col5  col6  col7
 * row0  --   1    --    2    --    3    --    4
 * row1  5    --    6    --    7    --    8    --
 * row2  --   9    --   10    --   11    --   12
 * row3  13   --   14    --   15    --   16    --
 * row4  --   17    --   18    --   19    --   20
 * row5  21   --   22    --   23    --   24    --
 * row6  --   25    --   26    --   27    --   28
 * row7  29   --   30    --   31    --   32    --
 *
 * Dark squares satisfy (row + col) % 2 === 1.
 * Even rows: dark squares are at odd columns (col 1, 3, 5, 7)
 * Odd rows:  dark squares are at even columns (col 0, 2, 4, 6)
 */

/**
 * Convert a board position to standard checkers notation (1-32).
 * Returns the square number for the given position.
 * The position must be on a dark square ((row + col) % 2 === 1).
 */
export function positionToNotation(pos: Position): number {
  // Each row has 4 dark squares. The notation number is:
  // row * 4 + (index of dark square within that row) + 1
  //
  // For even rows (0, 2, 4, 6): dark squares at col 1, 3, 5, 7 → index = (col - 1) / 2
  // For odd rows  (1, 3, 5, 7): dark squares at col 0, 2, 4, 6 → index = col / 2
  const darkSquareIndex = pos.row % 2 === 0
    ? (pos.col - 1) / 2
    : pos.col / 2;

  return pos.row * 4 + darkSquareIndex + 1;
}

/**
 * Convert standard checkers notation (1-32) to a board position.
 * Returns the Position { row, col } for the given square number.
 */
export function notationToPosition(n: number): Position {
  // Square numbers 1-32 map to rows 0-7, 4 squares per row.
  // n is 1-indexed, so subtract 1 for 0-indexed calculation.
  const zeroIndexed = n - 1;
  const row = Math.floor(zeroIndexed / 4);
  const indexInRow = zeroIndexed % 4;

  // For even rows (0, 2, 4, 6): dark squares at col 1, 3, 5, 7
  // For odd rows  (1, 3, 5, 7): dark squares at col 0, 2, 4, 6
  const col = row % 2 === 0
    ? indexInRow * 2 + 1
    : indexInRow * 2;

  return { row, col };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if two positions are equal
 */
export function positionsEqual(pos1: Position, pos2: Position): boolean {
  return pos1.row === pos2.row && pos1.col === pos2.col;
}

/**
 * Check if a position is within the board bounds
 */
export function isValidPosition(pos: Position): boolean {
  return pos.row >= 0 && pos.row <= 7 && pos.col >= 0 && pos.col <= 7;
}

/**
 * Check if a position is on a dark (playable) square
 */
export function isDarkSquare(pos: Position): boolean {
  return (pos.row + pos.col) % 2 === 1;
}
